import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { supportedLanguages, translations } from "./translations";
import { translateLiteral } from "./literalTranslations";

const STORAGE_KEY = "spellstudy.language";
const DEFAULT_LANGUAGE = "en";

const LanguageContext = createContext(null);

const isSupportedLanguage = (language) =>
  supportedLanguages.some((supported) => supported.code === language);

const getBrowserLanguage = () => {
  if (typeof navigator === "undefined") return DEFAULT_LANGUAGE;
  const browserLanguage = navigator.language?.split("-")[0];
  return isSupportedLanguage(browserLanguage) ? browserLanguage : DEFAULT_LANGUAGE;
};

const getInitialLanguage = () => {
  if (typeof window === "undefined") return DEFAULT_LANGUAGE;
  const storedLanguage = window.localStorage.getItem(STORAGE_KEY);
  return isSupportedLanguage(storedLanguage) ? storedLanguage : getBrowserLanguage();
};

const getTranslationValue = (language, key) => {
  return key.split(".").reduce((current, part) => current?.[part], translations[language]);
};

const interpolate = (value, params = {}) => {
  if (typeof value !== "string") return value;
  return value.replace(/\{(\w+)\}/g, (_, paramKey) => params[paramKey] ?? "");
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguageState] = useState(getInitialLanguage);

  const setLanguage = (nextLanguage) => {
    if (!isSupportedLanguage(nextLanguage)) return;
    setLanguageState(nextLanguage);
  };

  useEffect(() => {
    window.localStorage.setItem(STORAGE_KEY, language);
    document.documentElement.lang = language;
    document.documentElement.dir = "ltr";
  }, [language]);

  const value = useMemo(() => {
    const t = (key, params) => {
      const translatedValue =
        getTranslationValue(language, key) ??
        getTranslationValue(DEFAULT_LANGUAGE, key) ??
        key;

      return interpolate(translatedValue, params);
    };

    const lt = (value) => translateLiteral(value, language);

    return {
      language,
      setLanguage,
      supportedLanguages,
      t,
      lt,
    };
  }, [language]);

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};

export const useTranslation = () => {
  const context = useContext(LanguageContext);
  if (context) {
    return context;
  }

  return {
    language: DEFAULT_LANGUAGE,
    setLanguage: () => {},
    supportedLanguages,
    t: (key, params) => interpolate(getTranslationValue(DEFAULT_LANGUAGE, key) ?? key, params),
    lt: (value) => value,
  };
};
